import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Reservation,
  Member,
  Site,
  ScheduleEvent,
  PendingQuickAction,
  QuickActionPayment,
  ModificationPaymentFields,
  ModificationPreview,
} from '../../types';
import { SiteBlackout, SiteBlackoutUpdate } from '../../types/site';
import { useReservationData } from '../../contexts/ReservationDataContext';
import { getAllActiveSiteBlackouts, updateSiteBlackout, deleteSiteBlackout } from '../../services/blackoutService';
import { getScheduleEvents } from '../../services/scheduleEventService';
import { getMemberById, mapApiMember } from '../../services/memberService';
import { getStatusColor } from '../../utils/formatters';
import { getTodayString } from '../../utils/dateUtils';
import { MemberReview } from '../../types/memberReview';
import { listReviewsForMember } from '../../services/memberReviewService';
import ScheduleGrid from './ScheduleGrid';
import ReservationPopup from './ReservationPopup';
import PaymentPopup from './PaymentPopup';
// Real wizards with API integration
import CreateReservationWizard from './CreateReservationWizard';
import AdminCancellationWizard from './CancellationWizard/AdminCancellationWizard';
import ModifyReservationWizard from './ModifyReservationWizard';
import ResendAgreementModal from './ResendAgreementModal';
import CheckInOutModal from './CheckInOutModal';
import { undoCheckIn } from '../../services/reservationService';
import {
  getModifyPreview,
  executeModify,
  getSiteChangePreview,
  executeSiteChange,
  getRebookPreview,
  executeRebook,
} from '../../services/reservationModificationService';
import LoadingSpinner from '../../utils/LoadingSpinner';
import './ReservationSchedule.css';

// Grid layout constants (must match ScheduleGrid internals)
const REVIEW_BATCH_SIZE = 15;
const GRID_COL_PX = 45;   // day-column width for full-season date ranges
const SITE_COL_PX = 220;  // default site-name column width
const VISIBLE_DAYS = 30;  // estimated day columns visible in a typical viewport

// Map GridSite to grid Site type for compatibility
function mapGridSiteToSite(gridSite: { id: number; alias: string; name: string; siteType: string; section: string; maxLength?: number; seasonal?: boolean }): Site {
  return {
    id: gridSite.id,
    alias: gridSite.alias,
    name: gridSite.name,
    description: '',
    siteType: gridSite.siteType as 'RV' | 'Tent' | 'Cabin',
    section: gridSite.section,
    amenities: [],
    pricePerNight: 0,
    maxLength: gridSite.maxLength,
    isAvailable: true,
    seasonal: gridSite.seasonal,
  };
}

const ReservationSchedule: React.FC = () => {
  const navigate = useNavigate();

  // Use ReservationDataContext for all data
  const {
    state,
    visibleReservations,
    hasSearch,
    firstMatchId,
    setYear,
    setSearch,
    clearSearch,
    refreshReservations,
    onScroll,
    onReservationUpdated,
  } = useReservationData();

  const {
    sites: gridSites,
    sections,
    allDates,
    selectedYear,
    sitesLoading,
    sitesLoaded,
    reservationsLoading,
    reservationsError,
    searchCode,
    searchEmail,
    searchName,
  } = state;

  // Convert GridSite[] to Site[] for ScheduleGrid compatibility
  const sites = useMemo(() => gridSites.map(mapGridSiteToSite), [gridSites]);

  // Blackouts
  const [blackouts, setBlackouts] = useState<SiteBlackout[]>([]);
  // Schedule events
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);

  useEffect(() => {
    getAllActiveSiteBlackouts()
      .then(setBlackouts)
      .catch(err => console.error('Failed to load blackouts:', err));
    getScheduleEvents(true)
      .then(setScheduleEvents)
      .catch(err => console.error('Failed to load schedule events:', err));
  }, []);

  // Blackout edits from the schedule grid's popover. No try/catch on purpose —
  // the rejection has to reach the popover so it can show an inline error and
  // keep the manager's draft for a retry.
  const handleUpdateBlackout = useCallback(async (blackoutId: number, update: SiteBlackoutUpdate) => {
    const updated = await updateSiteBlackout(blackoutId, update);
    // This list holds only ACTIVE blackouts (getAllActiveSiteBlackouts), so a
    // deactivated record is dropped rather than kept with active: false — that
    // keeps the array's invariant identical to what the initial fetch returns.
    setBlackouts(prev =>
      updated.active ? prev.map(b => (b.id === blackoutId ? updated : b)) : prev.filter(b => b.id !== blackoutId)
    );
  }, []);

  const handleDeleteBlackout = useCallback(async (blackoutId: number) => {
    await deleteSiteBlackout(blackoutId);
    setBlackouts(prev => prev.filter(b => b.id !== blackoutId));
  }, []);

  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);

  // Lazy-loaded review map: reservationId → MemberReview
  const [reviewMap, setReviewMap] = useState<Map<number, MemberReview>>(new Map());
  const fetchedMemberIds = useRef<Set<number>>(new Set());

  const fetchReviewsForMemberIds = useCallback(async (memberIds: number[]) => {
    const newIds = memberIds.filter(id => !fetchedMemberIds.current.has(id));
    if (newIds.length === 0) return;
    const batch = newIds.slice(0, REVIEW_BATCH_SIZE);
    // Mark immediately to prevent duplicate fetches across concurrent calls
    batch.forEach(id => fetchedMemberIds.current.add(id));
    try {
      const results = await Promise.allSettled(batch.map(id => listReviewsForMember(id)));
      const reviews: MemberReview[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled') reviews.push(...r.value);
      }
      if (reviews.length > 0) {
        setReviewMap((prev: Map<number, MemberReview>) => {
          const next = new Map(prev);
          for (const review of reviews) {
            if (review.reservationId != null) next.set(review.reservationId, review);
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to fetch reservation reviews:', err);
    }
  }, []);

  // Reset review cache when year changes (different season = different reservations)
  useEffect(() => {
    setReviewMap(new Map());
    fetchedMemberIds.current = new Set();
  }, [selectedYear]);

  // Initial load: fetch reviews for reservations near today when data first arrives
  useEffect(() => {
    if (visibleReservations.length === 0 || allDates.length === 0) return;
    // Local today — toISOString() would roll over to tomorrow after 8pm ET.
    const todayStr = getTodayString();
    const todayIdx = allDates.findIndex(d => d >= todayStr);
    const startIdx = Math.max(0, todayIdx < 0 ? 0 : todayIdx);
    const endIdx = Math.min(allDates.length - 1, startIdx + VISIBLE_DAYS);
    const windowStart = allDates[startIdx];
    const windowEnd = allDates[endIdx];
    const memberIds = Array.from(
      new Set(
        visibleReservations
          .filter(r => r.memberId && r.checkIn <= windowEnd && r.checkOut >= windowStart)
          .map(r => r.memberId!)
      )
    );
    fetchReviewsForMemberIds(memberIds);
  }, [visibleReservations, allDates, fetchReviewsForMemberIds]);

  // Wizard states
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showCancelWizard, setShowCancelWizard] = useState(false);
  const [showModifyWizard, setShowModifyWizard] = useState(false);
  const [showResendAgreement, setShowResendAgreement] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [createInitialSelection, setCreateInitialSelection] = useState<{ siteAlias: string; checkIn: string } | undefined>(undefined);

  const loading = sitesLoading || reservationsLoading;
  const error = reservationsError;

  const handleSearchCodeChange = (value: string) => {
    setSearch({ searchCode: value });
  };

  const handleSearchEmailChange = (value: string) => {
    setSearch({ searchEmail: value });
  };

  const handleSearchNameChange = (value: string) => {
    setSearch({ searchName: value });
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handlePrevYear = () => setYear(selectedYear - 1);
  const handleNextYear = () => setYear(selectedYear + 1);

  const handleReservationClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setSelectedMember(null);
    if (reservation.memberId) {
      getMemberById(reservation.memberId)
        .then(api => setSelectedMember(mapApiMember(api)))
        .catch(err => console.error('Failed to load member for popup:', err));
    }
  };

  const handleClosePopup = () => {
    setSelectedReservation(null);
    setSelectedMember(null);
    setShowPaymentPopup(false);
  };

  const handleViewPayment = () => {
    setShowPaymentPopup(true);
  };

  const handleClosePayment = () => {
    setShowPaymentPopup(false);
  };

  const handleViewMember = (memberId: number) => {
    setSelectedReservation(null);
    navigate(`/members/${memberId}`);
  };

  const handleModifyReservation = () => {
    setShowModifyWizard(true);
  };

  const handleCancelReservation = () => {
    setShowCancelWizard(true);
  };

  const handleResendAgreement = () => {
    setShowResendAgreement(true);
  };

  const handleCheckIn = () => {
    setShowCheckInModal(true);
  };

  const handleCheckOut = () => {
    setShowCheckOutModal(true);
  };

  const handleUndoCheckIn = async (index: number) => {
    if (!selectedReservation) return;
    try {
      await undoCheckIn(
        selectedReservation.id,
        selectedReservation.specialRequests,
        index
      );
      setSelectedReservation(null);  // Close popup to show fresh data on next open
      handleWizardSuccess();
    } catch (err) {
      console.error('Failed to undo check-in:', err);
    }
  };

  const handleCreateReservation = () => {
    setCreateInitialSelection(undefined);
    setShowCreateWizard(true);
  };

  const handleEmptyCellClick = (site: Site, date: string) => {
    setCreateInitialSelection({ siteAlias: site.alias, checkIn: date });
    setShowCreateWizard(true);
  };

  const handleWizardSuccess = () => {
    // Invalidate and refresh reservation data after successful wizard action
    if (selectedReservation) {
      onReservationUpdated(selectedReservation.id);
    }
    refreshReservations();
  };

  // Occupancy fields are unchanged by grid quick actions, but the backend endpoints expect them
  // on every request (matching ModificationWizard's existing behavior for all modification types).
  const buildOccupancyPayload = (reservation: Reservation) => ({
    new_adult: reservation.adults,
    new_child: reservation.children,
    new_pets: reservation.pets,
    new_vehicles: reservation.vehicles,
    site_occupancies: [{
      site_alias: reservation.siteAlias,
      adults: reservation.adults,
      children: reservation.children,
      pets: reservation.pets,
      vehicles: reservation.vehicles,
    }],
  });

  // Mirrors ModificationWizard: 'payment_link' emails the guest a link, cash/check/card_terminal
  // record an in-person payment alongside the change, and 'none' leaves the balance due.
  const buildQuickActionPaymentFields = (payment: QuickActionPayment): ModificationPaymentFields => {
    switch (payment.method) {
      case 'payment_link':
        return { send_payment_link: true };
      case 'cash':
      case 'check':
        return { payment_method: payment.method, payment_status: payment.status };
      case 'card_terminal':
        return {
          payment_method: 'card_terminal',
          payment_status: payment.status,
          terminal_transaction_id: payment.terminalTransactionId,
        };
      default:
        return {};
    }
  };

  const handleLoadQuickActionPreview = useCallback(async (action: PendingQuickAction): Promise<ModificationPreview> => {
    const occupancy = buildOccupancyPayload(action.reservation);
    switch (action.type) {
      case 'extend-checkin':
      case 'extend-checkout':
      case 'move-dates':
        return getModifyPreview(action.reservation.id, {
          new_check_in: action.targetCheckIn,
          new_days: action.targetDays,
          ...occupancy,
        });
      case 'move-site':
        return getSiteChangePreview(action.reservation.id, {
          new_site_aliases: [action.targetSiteAlias],
          ...occupancy,
          site_occupancies: [{ ...occupancy.site_occupancies[0], site_alias: action.targetSiteAlias }],
        });
      case 'move-site-and-dates':
        return getRebookPreview(action.reservation.id, {
          new_site_aliases: [action.targetSiteAlias],
          new_check_in: action.targetCheckIn,
          new_days: action.targetDays,
          ...occupancy,
          site_occupancies: [{ ...occupancy.site_occupancies[0], site_alias: action.targetSiteAlias }],
        });
      default:
        throw new Error('Unsupported quick action type');
    }
  }, []);

  const handleExecuteQuickAction = useCallback(async (
    action: PendingQuickAction,
    reason: string,
    payment: QuickActionPayment
  ): Promise<void> => {
    const occupancy = buildOccupancyPayload(action.reservation);
    const base = { reason, ...buildQuickActionPaymentFields(payment) };
    switch (action.type) {
      case 'extend-checkin':
      case 'extend-checkout':
      case 'move-dates':
        await executeModify(action.reservation.id, {
          new_check_in: action.targetCheckIn,
          new_days: action.targetDays,
          ...occupancy,
          ...base,
        });
        break;
      case 'move-site':
        await executeSiteChange(action.reservation.id, {
          new_site_aliases: [action.targetSiteAlias],
          ...occupancy,
          site_occupancies: [{ ...occupancy.site_occupancies[0], site_alias: action.targetSiteAlias }],
          ...base,
        });
        break;
      case 'move-site-and-dates':
        await executeRebook(action.reservation.id, {
          new_site_aliases: [action.targetSiteAlias],
          new_check_in: action.targetCheckIn,
          new_days: action.targetDays,
          ...occupancy,
          site_occupancies: [{ ...occupancy.site_occupancies[0], site_alias: action.targetSiteAlias }],
          ...base,
        });
        break;
      default:
        throw new Error('Unsupported quick action type');
    }
    onReservationUpdated(action.reservation.id);
    refreshReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReservationUpdated, refreshReservations]);

  const handleScroll = useCallback((scrollLeft: number, scrollTop: number) => {
    onScroll(scrollLeft, scrollTop);
    if (allDates.length === 0) return;
    // Estimate which date range is visible in the horizontal viewport
    const adjustedScroll = Math.max(0, scrollLeft - SITE_COL_PX);
    const firstIdx = Math.floor(adjustedScroll / GRID_COL_PX);
    const lastIdx = Math.min(allDates.length - 1, firstIdx + VISIBLE_DAYS);
    const windowStart = allDates[firstIdx];
    const windowEnd = allDates[lastIdx];
    if (!windowStart || !windowEnd) return;
    const memberIds = Array.from(
      new Set(
        visibleReservations
          .filter(r => r.memberId && r.checkIn <= windowEnd && r.checkOut >= windowStart)
          .map(r => r.memberId!)
      )
    );
    fetchReviewsForMemberIds(memberIds);
  }, [onScroll, allDates, visibleReservations, fetchReviewsForMemberIds]);

  return (
    <div className="reservation-schedule">
      <div className="schedule-header">
        <div className="schedule-title-section">
          <h1 className="schedule-title">Reservation Schedule</h1>
          <span className="schedule-date-range">Apr 15 – Oct 1, {selectedYear}</span>
        </div>

        <div className="schedule-controls">
          <button className="btn btn-secondary" onClick={handlePrevYear}>
            ← {selectedYear - 1}
          </button>
          <span className="year-label">{selectedYear} Season</span>
          <button className="btn btn-secondary" onClick={handleNextYear}>
            {selectedYear + 1} →
          </button>
          <button className="btn btn-success" onClick={handleCreateReservation}>
            + NEW RESERVATION
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => refreshReservations()}
            disabled={reservationsLoading}
            title="Refresh reservations"
          >
            {reservationsLoading ? '↻ Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      <div className="schedule-search">
        <div className="search-field">
          <label>Confirmation Code</label>
          <input
            type="text"
            value={searchCode}
            onChange={(e) => handleSearchCodeChange(e.target.value)}
            placeholder="e.g. ABC123"
          />
        </div>
        <div className="search-field">
          <label>Email</label>
          <input
            type="text"
            value={searchEmail}
            onChange={(e) => handleSearchEmailChange(e.target.value)}
            placeholder="guest@email.com"
          />
        </div>
        <div className="search-field">
          <label>Last Name</label>
          <input
            type="text"
            value={searchName}
            onChange={(e) => handleSearchNameChange(e.target.value)}
            placeholder="Smith"
          />
        </div>
        {hasSearch && (
          <button className="search-clear" onClick={handleClearSearch}>
            Clear
          </button>
        )}
      </div>

      <div className="schedule-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: getStatusColor('confirmed') }}></span>
          <span>Confirmed</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: getStatusColor('pending_payment') }}></span>
          <span>Awaiting Payment</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#3b82f6' }}></span>
          <span>Checked In</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#6b7280' }}></span>
          <span>Checked Out</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#a927f5' }}></span>
          <span>Modified</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#d97706' }}></span>
          <span>Pay Cash/Check</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#dc2626' }}></span>
          <span>Do Not Welcome Back</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#92400e' }}></span>
          <span>1-Star (Restricted)</span>
        </div>
      </div>

      {error && (
        <div className="schedule-error" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
          {error}
          <button
            className="btn btn-secondary"
            style={{ marginLeft: '12px' }}
            onClick={() => refreshReservations()}
          >
            Retry
          </button>
        </div>
      )}

      <div className="schedule-grid-wrapper-outer">
        {loading && (
          <div className="schedule-loading-overlay">
            <LoadingSpinner size="large" message="Loading reservations..." />
          </div>
        )}

        {sitesLoaded && (
          <ScheduleGrid
            sites={sites}
            sections={sections}
            dates={allDates}
            reservations={visibleReservations.filter(r => r.status !== 'cancelled')}
            onReservationClick={handleReservationClick}
            highlightReservationId={firstMatchId}
            onScroll={handleScroll}
            blackouts={blackouts}
            scheduleEvents={scheduleEvents}
            reservationReviews={reviewMap}
            onEmptyCellClick={handleEmptyCellClick}
            onLoadQuickActionPreview={handleLoadQuickActionPreview}
            onExecuteQuickAction={handleExecuteQuickAction}
            onUpdateBlackout={handleUpdateBlackout}
            onDeleteBlackout={handleDeleteBlackout}
          />
        )}
      </div>

      {selectedReservation && !showPaymentPopup && (
        <ReservationPopup
          reservation={selectedReservation}
          member={selectedMember}
          onClose={handleClosePopup}
          onViewMember={handleViewMember}
          onViewPayment={handleViewPayment}
          onModify={handleModifyReservation}
          onCancel={handleCancelReservation}
          onResendAgreement={handleResendAgreement}
          onCheckIn={handleCheckIn}
          onCheckOut={handleCheckOut}
          onUndoCheckIn={handleUndoCheckIn}
        />
      )}

      {showPaymentPopup && selectedReservation && (
        <PaymentPopup
          reservation={selectedReservation}
          onClose={handleClosePayment}
        />
      )}

      {/* Wizards with API Integration */}
      <CreateReservationWizard
        isOpen={showCreateWizard}
        onClose={() => {
          setShowCreateWizard(false);
          setCreateInitialSelection(undefined);
        }}
        onSuccess={handleWizardSuccess}
        initialSelection={createInitialSelection}
      />

      <AdminCancellationWizard
        isOpen={showCancelWizard}
        onClose={() => {
          setShowCancelWizard(false);
          setSelectedReservation(null);
        }}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      <ModifyReservationWizard
        isOpen={showModifyWizard}
        onClose={() => {
          setShowModifyWizard(false);
          setSelectedReservation(null);
        }}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      {showResendAgreement && selectedReservation && (
        <ResendAgreementModal
          reservation={selectedReservation}
          onClose={() => setShowResendAgreement(false)}
          onSuccess={handleWizardSuccess}
        />
      )}

      {showCheckInModal && selectedReservation && (
        <CheckInOutModal
          reservation={selectedReservation}
          mode="checkin"
          onClose={() => {
            setShowCheckInModal(false);
            setSelectedReservation(null);
          }}
          onSuccess={handleWizardSuccess}
        />
      )}

      {showCheckOutModal && selectedReservation && (
        <CheckInOutModal
          reservation={selectedReservation}
          mode="checkout"
          onClose={() => {
            setShowCheckOutModal(false);
            setSelectedReservation(null);
          }}
          onSuccess={handleWizardSuccess}
        />
      )}
    </div>
  );
};

export default ReservationSchedule;
